import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import {
  containsLineBreaks,
  convertLineBreaksToCommas,
} from '@src/utility/customVocabulary';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import Accordion from './Accordion';
import Textarea from './Textarea';

const logger = LogManager.getLogger('BrandKit:TextareaSection');

const VOCABULARY_NEWLINE_ERROR =
  'Separate terms with commas, not new lines, or the transcription will treat the whole list as a single term.';

const SAVE_FAILED_ERROR = 'Failed to save. Please try again.';

type Props = {
  property: 'brandVoice' | 'avoidWords' | 'customVocabulary';
};

const BrandKitTextareaSection = observer((props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();

  const { property } = props;

  const branding = videoCreator.organization?.branding;
  const data = branding?.[property] as string | undefined;
  const [value, setValue] = useState(data || '');

  useEffect(() => {
    if (data) setValue(data);
  }, [Boolean(data)]);

  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isVocabulary = property === 'customVocabulary';

  const persist = async (toSave: string) => {
    setIsSaving(true);
    setIsSaved(false);
    try {
      const organization = videoCreator.organization;
      organization!.branding = {
        ...organization!.branding!,
        [property]: toSave,
      };
      await datoClientStore.albumRepository?.update(organization!);
      setIsSaved(true);
    } catch (err) {
      logger.error('Failed to save brand kit field', {
        property,
        ...extractErrorInfo(err),
      });
      setError(SAVE_FAILED_ERROR);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    const trimmed = value.trim();
    if (isVocabulary && containsLineBreaks(trimmed)) {
      setError(VOCABULARY_NEWLINE_ERROR);
      return;
    }
    await persist(trimmed);
  };

  const handleFix = async () => {
    const fixed = convertLineBreaksToCommas(value);
    setValue(fixed);
    setError(null);
    await persist(fixed);
  };

  const { id, title, placeholder, tips, subtitle } =
    getPropsFromProperty(property);

  return (
    <Accordion id={id} title={title} isSaved={isSaved}>
      <Textarea
        value={value}
        defaultValue={data}
        onChange={e => {
          setValue(e.target.value);
          setError(null);
        }}
        onSave={handleSave}
        isSaving={isSaving}
        placeholder={placeholder}
        tips={tips}
        onCancel={() => {
          setValue(data || '');
          setError(null);
        }}
        subtitle={subtitle}
        error={error || undefined}
        onFix={
          isVocabulary && error === VOCABULARY_NEWLINE_ERROR
            ? handleFix
            : undefined
        }
        fixLabel="Convert to Commas"
      />
    </Accordion>
  );
});

const getPropsFromProperty = (property: string) => {
  switch (property) {
    case 'brandVoice':
      return {
        id: 'brand-voice',
        title: 'Brand Voice',
        placeholder:
          'Describe your brand’s unique personality and how you communicate with your audience. Your voice is an important way to attract people to your brand, create connections, and build trust with them. For example, “our voice is confident, casual, and friendly.”',
      };
    case 'avoidWords':
      return {
        id: 'avoid-words',
        title: 'Avoid Words (Beta)',
        placeholder:
          'Provide a list of words to avoid or provide a list of words to avoid + recommended synonyms. An example of the latter,\n\nAvoid tree and replace with arbor\nAvoid person and replace with human',
      };
    case 'customVocabulary':
      return {
        id: 'company-dictionary',
        title: 'Company Dictionary',
        placeholder:
          'Write in proper spellings in a comma separated format like such:\nNew York Knicks, Jalen Brunson, Karl-Anthony Towns, OG Anunoby',
        subtitle: 'Correct Spelling',
        tips: 'After updating the Company Dictionary please wait a few minutes before uploading a video',
      };
    default:
      return {
        id: '',
        title: '',
        placeholder: '',
        tips: '',
      };
  }
};

export default BrandKitTextareaSection;
